import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Wine, MapPin, Heart, User, LogOut, Menu, X, Plus, Map as MapIcon, Shield, Crown, Layers } from 'lucide-react';

export const Layout = ({ children }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Public base menu — only Vineyards & Wines visible to logged-out visitors
  const publicLinks = [
    { to: '/vineyards', label: 'Vineyards', icon: MapPin },
    { to: '/wines', label: 'Wines', icon: Wine },
  ];
  // Extra links shown only when signed in
  const memberLinks = [
    { to: '/map', label: 'Map', icon: MapIcon },
    { to: '/cellar', label: 'Cellar', icon: Layers },
    { to: '/favorites', label: 'Favorites', icon: Heart },
  ];
  const navLinks = isAuthenticated ? [...publicLinks, ...memberLinks] : publicLinks;

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-stone-950 noise-bg">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/50 backdrop-blur-xl border-b border-white/5 h-20" data-testid="main-navigation">
        <div className="container mx-auto px-4 md:px-8 h-full flex items-center justify-between">
          {/* Logo */}
          <a href="https://vineyardsandwine.com/" target="_blank" rel="noopener noreferrer" className="flex items-center" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_winery-finder/artifacts/i4imw511_Vineyards%26Wine%20%281%29.png" 
              alt="VINEYARDS & WINE" 
              className="w-[200px] h-[120px] object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={`nav-link flex items-center gap-2 text-sm uppercase tracking-widest ${
                  isActive(to) ? 'text-[#d4af37]' : ''
                }`}
                data-testid={`nav-link-${label.toLowerCase()}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            ))}
            {user?.is_admin && (
              <Link
                to="/admin"
                className={`nav-link flex items-center gap-2 text-sm uppercase tracking-widest ${
                  isActive('/admin') ? 'text-[#d4af37]' : 'text-[#d4af37]/80'
                }`}
                data-testid="nav-link-admin"
              >
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="text-stone-400 hover:text-stone-100 gap-2"
                      data-testid="add-new-dropdown"
                    >
                      <Plus className="w-4 h-4" />
                      Add New
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-stone-900 border-stone-700">
                    <DropdownMenuItem
                      onClick={() => navigate('/vineyards/new')}
                      className="cursor-pointer hover:bg-stone-800"
                      data-testid="add-vineyard-btn"
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Add Vineyard
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/wines/new')}
                      className="cursor-pointer hover:bg-stone-800"
                      data-testid="add-wine-btn"
                    >
                      <Wine className="w-4 h-4 mr-2" />
                      Add Wine
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="text-stone-400 hover:text-stone-100 gap-2"
                      data-testid="user-menu-dropdown"
                    >
                      <User className="w-4 h-4" />
                      {user?.name}
                      {user?.is_admin && (
                        <Crown className="w-3 h-3 text-[#d4af37] ml-1" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-stone-900 border-stone-700">
                    <DropdownMenuItem
                      onClick={() => navigate('/profile')}
                      className="cursor-pointer hover:bg-stone-800"
                      data-testid="profile-menu-item"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-stone-700" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="cursor-pointer hover:bg-stone-800 text-red-400"
                      data-testid="logout-menu-item"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-stone-400 hover:text-stone-100"
                  data-testid="login-nav-btn"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs px-6"
                  data-testid="register-nav-btn"
                >
                  Join Now
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-stone-400"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-stone-950/95 backdrop-blur-xl border-b border-white/5" data-testid="mobile-menu">
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navLinks.map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center gap-3 text-stone-300 py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </Link>
              ))}
              {isAuthenticated ? (
                <>
                  {user?.is_admin && (
                    <Link
                      to="/admin"
                      className="flex items-center gap-3 text-[#d4af37] py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Shield className="w-5 h-5" />
                      Admin
                    </Link>
                  )}
                  <Link
                    to="/vineyards/new"
                    className="flex items-center gap-3 text-stone-300 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Plus className="w-5 h-5" />
                    Add Vineyard
                  </Link>
                  <Link
                    to="/wines/new"
                    className="flex items-center gap-3 text-stone-300 py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Plus className="w-5 h-5" />
                    Add Wine
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 text-red-400 py-2"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-4 pt-4 border-t border-stone-800">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      navigate('/login');
                      setMobileMenuOpen(false);
                    }}
                    className="flex-1"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={() => {
                      navigate('/register');
                      setMobileMenuOpen(false);
                    }}
                    className="flex-1 bg-[#d4af37] text-stone-950"
                  >
                    Join
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-20">
        {user?.status === 'pending' && (
          <div
            className="bg-[#d4af37]/10 border-b border-[#d4af37]/30 text-[#d4af37] px-4 py-3"
            data-testid="pending-approval-banner"
          >
            <div className="container mx-auto px-4 md:px-8 text-sm flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#d4af37]/20 flex-shrink-0">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              </span>
              <span>
                Your account is <span className="font-semibold">pending admin approval</span>.
                You can browse and discover, but cannot add vineyards, wines, rate, or favorite until approved.
              </span>
            </div>
          </div>
        )}
        {user?.status === 'rejected' && (
          <div
            className="bg-red-900/30 border-b border-red-500/30 text-red-300 px-4 py-3"
            data-testid="rejected-banner"
          >
            <div className="container mx-auto px-4 md:px-8 text-sm">
              Your account has been <span className="font-semibold">rejected</span>. Please contact an administrator.
            </div>
          </div>
        )}
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-black/50 border-t border-white/5 py-12 mt-20" data-testid="footer">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_winery-finder/artifacts/i4imw511_Vineyards%26Wine%20%281%29.png" 
              alt="VINEYARDS & WINE" 
              className="w-[160px] h-auto opacity-70"
            />
            <p className="text-stone-600 text-sm">
              © {new Date().getFullYear()} All rights reserved. Discover the finest vineyards.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
